import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/logo";
import { LanguageSwitcher } from "@/components/language-switcher";

export function MobileMarketingHeader() {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const sections: { title: string; links: { href: string; label: string; internal?: boolean }[] }[] = [
    {
      title: t("nav.product", { defaultValue: "Prodotto" }),
      links: [
        { href: "/features", label: t("nav.features"), internal: true },
        { href: "/moduli", label: "Moduli", internal: true },
        { href: "/demo", label: "Demo", internal: true },
        { href: "/#pricing", label: t("nav.pricing") },
        { href: "/#faq", label: t("nav.faq") },
      ],
    },
    {
      title: t("nav.company", { defaultValue: "Azienda" }),
      links: [
        { href: "/sicurezza", label: t("nav.security"), internal: true },
        { href: "/chi-siamo", label: "Chi siamo", internal: true },
        { href: "/contatti", label: "Contatti", internal: true },
      ],
    },
    {
      title: "Account",
      links: [
        { href: "/app", label: "Area riservata", internal: true },
      ],
    },
  ];

  return (
    <>
      <div className="fixed inset-x-0 top-0 z-50 mob-blur border-b mob-hairline pt-[env(safe-area-inset-top)] md:hidden">
        <div className="flex h-[68px] items-center justify-between gap-2 px-5">
          <Link
            to="/"
            aria-label="lexaipro"
            className="mob-tap flex shrink-0 items-center gap-2"
          >
            <Logo variant="light" kind="wordmark" size={34} asLink={false} />
            <span className="hidden rounded-full border border-white/15 bg-white/[0.04] px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-[0.14em] text-white/70 xs:inline">
              {t("nav.beta")}
            </span>
          </Link>
          <div className="flex min-w-0 items-center gap-2">
            <Link
              to="/signup"
              className="mob-tap inline-flex h-11 shrink-0 items-center rounded-full bg-white px-4 text-[14px] font-semibold text-black"
            >
              {t("nav.signup")}
            </Link>
            <button
              aria-label={t("mobile.actions.menu")}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="mob-tap inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border mob-hairline-strong text-white"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Full-screen drawer */}
      {open && (
        <div className="fixed inset-0 z-[60] mob-surface-0 pt-[calc(env(safe-area-inset-top)+68px)] text-white md:hidden overflow-y-auto">
          <button
            aria-label={t("mobile.actions.close")}
            onClick={() => setOpen(false)}
            className="mob-tap absolute right-5 top-[calc(env(safe-area-inset-top)+18px)] inline-flex h-11 w-11 items-center justify-center rounded-full border mob-hairline-strong"
          >
            <X className="h-5 w-5" />
          </button>

          <nav className="px-5 pt-4 pb-32">
            {sections.map((section) => (
              <div key={section.title} className="mb-6">
                <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-white/45">
                  {section.title}
                </div>
                {section.links.map((l) =>
                  l.internal ? (
                    <Link
                      key={l.href}
                      to={l.href as any}
                      onClick={() => setOpen(false)}
                      className="mob-tap block py-3 text-[22px] font-semibold tracking-tight"
                    >
                      {l.label}
                    </Link>
                  ) : (
                    <a
                      key={l.href}
                      href={l.href}
                      onClick={() => setOpen(false)}
                      className="mob-tap block py-3 text-[22px] font-semibold tracking-tight"
                    >
                      {l.label}
                    </a>
                  ),
                )}
              </div>
            ))}
          </nav>

          <div className="fixed inset-x-0 bottom-0 mob-blur border-t mob-hairline px-5 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-[0.14em] text-white/45">
                {t("mobile.drawer.language")}
              </span>
              <LanguageSwitcher variant="dark" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="mob-tap inline-flex h-12 items-center justify-center rounded-full border mob-hairline-strong text-[15px] font-medium"
              >
                {t("nav.login")}
              </Link>
              <Link
                to="/signup"
                onClick={() => setOpen(false)}
                className="mob-tap inline-flex h-12 items-center justify-center rounded-full bg-white text-[15px] font-semibold text-black"
              >
                {t("nav.signup")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
